import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Check, Send } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 1000));
    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <section id="contact" className="zone-resolution section-tight border-t border-border">
        <div className="container-narrow">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="bg-improvement-light/30 rounded-md border border-improvement/20 p-8 text-center">
              <div className="w-10 h-10 rounded-full bg-improvement/20 flex items-center justify-center mx-auto mb-4">
                <Check size={20} className="text-improvement" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground mb-2">Message Received</h3>
              <p className="text-sm text-muted-foreground mb-3">
                In a real scenario, we would review and respond within 2 business days.
              </p>
              <p className="text-xs text-muted-foreground italic">This is a demo. Messages are not sent.</p>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="zone-resolution section-tight border-t border-border">
      <div className="container-narrow">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-5">
          <p className="text-[11px] font-medium text-muted-foreground tracking-wide uppercase mb-1.5">Get Started</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-2">
            Discuss Your Product
          </h2>
          <p className="text-sm text-muted-foreground max-w-md">
            Tell us where you are experiencing friction.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <div className="bg-card rounded-md border border-border p-5 md:p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-sm">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Jane Smith"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="bg-background"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-sm">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="jane@company.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="bg-background"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="message" className="text-sm">Where do users get confused?</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Describe the friction points..."
                  value={form.message}
                  onChange={handleChange}
                  rows={3}
                  className="bg-background resize-none"
                />
              </div>
              <Button type="submit" variant="clarity" size="default" disabled={submitting} className="w-full sm:w-auto">
                {submitting ? (
                  <><span className="w-3.5 h-3.5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />Sending...</>
                ) : (
                  <><Send size={14} className="mr-1.5" />Send Message</>
                )}
              </Button>
            </form>
          </div>
        </motion.div>

        <motion.p 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }} 
          variants={fadeUp}
          className="text-center text-xs text-muted-foreground mt-4 italic"
        >
          This is a demo. Messages are not sent.
        </motion.p>
      </div>
    </section>
  );
};

export default ContactSection;
